import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Building2, Clock, MessageCircle, CreditCard, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FIELDS = [
  { key: "clinic_name", label: "Nombre de la clínica", group: "clinic" },
  { key: "clinic_address", label: "Dirección", group: "clinic" },
  { key: "clinic_phone", label: "Teléfono", group: "clinic" },
  { key: "clinic_email", label: "Email", group: "clinic" },
  { key: "whatsapp_number", label: "WhatsApp (sin +)", group: "contact", placeholder: "34742086596" },
  { key: "wise_pay_link", label: "Link de Wise", group: "contact" },
  { key: "schedule_start", label: "Hora inicio (HH:MM)", group: "schedule" },
  { key: "schedule_end", label: "Hora fin (HH:MM)", group: "schedule" },
];

export default function AdminSettings() {
  const settings = useSettings();
  const [form, setForm] = useState({});
  const [overview, setOverview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("clinic");

  useEffect(() => { setForm(settings || {}); }, [settings]);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (only) => {
    setSaving(true);
    try {
      const payload = only
        ? Object.fromEntries(only.map((k) => [k, form[k]]).filter(([_, v]) => v !== undefined && v !== ""))
        : { ...form };
      // Cast types
      if (payload.consultation_price_eur != null && payload.consultation_price_eur !== "")
        payload.consultation_price_eur = parseFloat(payload.consultation_price_eur);
      if (payload.default_slot_minutes != null && payload.default_slot_minutes !== "")
        payload.default_slot_minutes = parseInt(payload.default_slot_minutes, 10);
      await api.put("/settings", payload);
      settings.refresh && settings.refresh();
      toast.success("Ajustes guardados");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-settings-page">
      <div>
        <h1 className="font-display text-3xl flex items-center gap-3">
          <SettingsIcon className="w-7 h-7 text-emerald-700" /> Panel de control
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Controla todos los aspectos de la consulta del Dr. Dimy Laguerre.
        </p>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-overview">
          {[
            { label: "Pacientes", value: overview.patients },
            { label: "Citas totales", value: overview.appointments },
            { label: "Recetas", value: overview.prescriptions },
            { label: "Exámenes", value: overview.exam_orders },
          ].map((s) => (
            <Card key={s.label} className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
              <CardContent className="p-4">
                <div className="text-3xl font-bold text-emerald-800">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="clinic" data-testid="tab-clinic"><Building2 className="w-4 h-4 mr-1" /> Clínica</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule"><Clock className="w-4 h-4 mr-1" /> Horarios</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard className="w-4 h-4 mr-1" /> Pagos</TabsTrigger>
          <TabsTrigger value="public" data-testid="tab-public"><MessageCircle className="w-4 h-4 mr-1" /> Público</TabsTrigger>
        </TabsList>

        <TabsContent value="clinic" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Datos de la clínica</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {FIELDS.filter((f) => f.group === "clinic").map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder || ""} data-testid={`field-${f.key}`} />
                </div>
              ))}
              <Button onClick={() => save(["clinic_name", "clinic_address", "clinic_phone", "clinic_email"])} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-clinic-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : "Guardar"}
              </Button>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader><CardTitle>Contacto</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {FIELDS.filter((f) => f.group === "contact").map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder || ""} data-testid={`field-${f.key}`} />
                </div>
              ))}
              <Button onClick={() => save(["whatsapp_number", "wise_pay_link"])} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-contact-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : "Guardar"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Horarios y slots</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.filter((f) => f.group === "schedule").map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}</Label>
                    <Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder="HH:MM" data-testid={`field-${f.key}`} />
                  </div>
                ))}
              </div>
              <div>
                <Label>Duración por slot (minutos)</Label>
                <Select value={String(form.default_slot_minutes || 15)} onValueChange={(v) => set("default_slot_minutes", v)}>
                  <SelectTrigger data-testid="field-default_slot_minutes"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 30, 45, 60].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => save(["schedule_start", "schedule_end", "default_slot_minutes"])} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-schedule-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : "Guardar"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Precio y pagos</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Precio de la consulta (EUR)</Label>
                <Input type="number" min="1" step="0.5" value={form.consultation_price_eur || 20} onChange={(e) => set("consultation_price_eur", e.target.value)} data-testid="field-consultation_price_eur" />
              </div>
              <Button onClick={() => save(["consultation_price_eur"])} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-price-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : "Guardar precio"}
              </Button>
              <div className="text-xs text-muted-foreground border-t pt-3">
                La clave Stripe se gestiona desde el perfil del doctor. Wise se gestiona arriba en Contacto.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="public" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Servicios públicos</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between border rounded-lg p-3">
                <div>
                  <div className="font-medium">Chat público con IA</div>
                  <div className="text-xs text-muted-foreground">Permite a visitantes chatear sin registrarse en /chat</div>
                </div>
                <Switch checked={!!form.allow_public_chat} onCheckedChange={(v) => set("allow_public_chat", v)} data-testid="switch-public-chat" />
              </div>
              <div className="flex items-center justify-between border rounded-lg p-3">
                <div>
                  <div className="font-medium">Registro público de pacientes</div>
                  <div className="text-xs text-muted-foreground">Si está desactivado, solo el admin puede crear cuentas</div>
                </div>
                <Switch checked={!!form.allow_public_register} onCheckedChange={(v) => set("allow_public_register", v)} data-testid="switch-public-register" />
              </div>
              <Button onClick={() => save(["allow_public_chat", "allow_public_register"])} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-public-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : "Guardar"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
